'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

const supabase = createClient();

interface Abbonamento {
  id: string;
  stato: string;
  periodo: string;
  data_inizio: string;
  data_fine: string | null;
  data_prossimo_rinnovo: string | null;
  importo: number | null;
  payment_status: string;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  piani_prezzi: {
    nome: string;
    slug: string;
    prezzo_mensile: number;
    prezzo_annuale: number;
    max_dispositivi: number | null;
    max_utenti: number | null;
  } | null;
}

const STATO_COLORS: Record<string, string> = {
  attivo: 'bg-green-500/15 text-green-400 border-green-500/30',
  trial: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  sospeso: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  cancellato: 'bg-red-500/15 text-red-400 border-red-500/30',
  scaduto: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

export default function SubscriptionPage() {
  const { profile } = useAuth();
  const [abbonamento, setAbbonamento] = useState<Abbonamento | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [message, setMessage] = useState('');

  const fetchData = useCallback(async () => {
    if (!profile?.struttura_id) { setLoading(false); return; }
    setLoading(true);
    const { data } = await supabase
      .from('abbonamenti')
      .select('*, piani_prezzi(nome, slug, prezzo_mensile, prezzo_annuale, max_dispositivi, max_utenti)')
      .eq('struttura_id', profile.struttura_id)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();
    setAbbonamento(data as any);
    setLoading(false);
  }, [profile?.struttura_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancelSubscription = async () => {
    if (!abbonamento?.stripe_subscription_id) return;
    if (!confirm('Sei sicuro di voler cancellare l\'abbonamento? Il servizio rimarrà attivo fino alla fine del periodo corrente.')) return;
    setCancelling(true);
    try {
      const res = await fetch('/api/stripe/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription_id: abbonamento.stripe_subscription_id }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('Abbonamento cancellato. Rimarrà attivo fino alla fine del periodo.');
        fetchData();
      } else {
        setMessage('Errore: ' + (data.error || 'Impossibile cancellare'));
      }
    } catch {
      setMessage('Errore di rete');
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return 'N/D';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const getDaysRemaining = () => {
    if (!abbonamento?.data_prossimo_rinnovo) return null;
    const diff = new Date(abbonamento.data_prossimo_rinnovo).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Stato Abbonamento</h1>
            <p className="text-secondary-color text-sm mt-0.5">Gestisci il tuo piano e la fatturazione</p>
          </div>
          <Link href="/prezzi" className="ml-auto flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-sky-400 rounded-xl text-sm font-semibold text-white transition-colors">
            <Icon name="ArrowUpCircleIcon" size={16} />
            Cambia Piano
          </Link>
        </div>

        {message && (
          <div className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${message.startsWith('Errore') ? 'bg-red-500/10 border-red-500/25 text-red-400' : 'bg-green-500/10 border-green-500/25 text-green-400'}`}>
            <Icon name={message.startsWith('Errore') ? 'ExclamationCircleIcon' : 'CheckCircleIcon'} size={18} className="flex-shrink-0" />
            <p className="text-sm font-medium">{message}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !profile?.struttura_id ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center space-y-4">
            <Icon name="BuildingOffice2Icon" size={48} className="mx-auto text-muted-color opacity-40" />
            <h2 className="text-lg font-bold text-white">Struttura non configurata</h2>
            <p className="text-secondary-color text-sm">
              Per gestire l&apos;abbonamento devi prima completare la configurazione della tua struttura.
            </p>
            <Link href="/onboarding" className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 hover:bg-sky-400 rounded-xl text-sm font-semibold text-white transition-colors">
              <Icon name="ArrowRightIcon" size={16} />
              Configura la struttura
            </Link>
          </div>
        ) : !abbonamento ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center space-y-4">
            <Icon name="CreditCardIcon" size={48} className="mx-auto text-muted-color opacity-40" />
            <h2 className="text-lg font-bold text-white">Nessun abbonamento attivo</h2>
            <p className="text-secondary-color text-sm">Attiva un piano per sbloccare tutte le funzionalità.</p>
            <Link href="/prezzi" className="inline-flex items-center gap-2 px-6 py-3 bg-sky-500 hover:bg-sky-400 rounded-xl text-sm font-semibold text-white transition-colors">
              <Icon name="SparklesIcon" size={16} />
              Scegli un Piano
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current Plan Card */}
            <div className="bg-surface border border-border rounded-xl p-6 space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">{abbonamento.piani_prezzi?.nome || 'Piano Corrente'}</h2>
                  <p className="text-secondary-color text-sm mt-0.5">
                    {abbonamento.periodo === 'mensile' ? 'Fatturazione mensile' : abbonamento.periodo === 'annuale' ? 'Fatturazione annuale' : abbonamento.periodo}
                  </p>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full border uppercase ${STATO_COLORS[abbonamento.stato] || STATO_COLORS.sospeso}`}>
                  {abbonamento.stato}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Importo', value: abbonamento.importo ? `€${abbonamento.importo.toFixed(2)}` : 'Gratuito', icon: 'CurrencyEuroIcon' },
                  { label: 'Prossimo Rinnovo', value: formatDate(abbonamento.data_prossimo_rinnovo), icon: 'CalendarIcon' },
                  { label: 'Attivo dal', value: formatDate(abbonamento.data_inizio), icon: 'ClockIcon' },
                  { label: 'Stato Pagamento', value: abbonamento.payment_status || 'N/D', icon: 'CheckBadgeIcon' },
                ].map(item => (
                  <div key={item.label} className="bg-surface-elevated rounded-xl p-4 flex items-center gap-3">
                    <Icon name={item.icon as any} size={18} className="text-sky-400 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-muted-color">{item.label}</p>
                      <p className="text-sm font-semibold text-white capitalize">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Days remaining warning */}
              {getDaysRemaining() !== null && getDaysRemaining()! <= 7 && (
                <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
                  <Icon name="ExclamationTriangleIcon" size={18} className="text-amber-400 flex-shrink-0" />
                  <p className="text-sm text-amber-400 font-medium">
                    Il tuo abbonamento scade tra <strong>{getDaysRemaining()} giorni</strong>. Rinnova per continuare ad usare il servizio.
                  </p>
                </div>
              )}

              {/* Plan limits */}
              {abbonamento.piani_prezzi && (
                <div className="border-t border-border pt-4 grid grid-cols-2 gap-3">
                  <div className="text-sm">
                    <span className="text-muted-color">Max Dispositivi: </span>
                    <span className="text-white font-semibold">{abbonamento.piani_prezzi.max_dispositivi ?? 'Illimitati'}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-color">Max Utenti: </span>
                    <span className="text-white font-semibold">{abbonamento.piani_prezzi.max_utenti ?? 'Illimitati'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Stripe ID info */}
            {abbonamento.stripe_subscription_id && (
              <div className="bg-surface border border-border rounded-xl p-4">
                <p className="text-xs text-muted-color mb-1">ID Abbonamento Stripe</p>
                <p className="text-sm font-mono text-secondary-color">{abbonamento.stripe_subscription_id}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Link href="/prezzi" className="flex items-center gap-2 px-4 py-2.5 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 rounded-xl text-sm font-semibold text-sky-400 transition-colors">
                <Icon name="ArrowUpCircleIcon" size={16} />
                Aggiorna Piano
              </Link>
              {abbonamento.stripe_subscription_id && abbonamento.stato === 'attivo' && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl text-sm font-semibold text-red-400 transition-colors disabled:opacity-50"
                >
                  <Icon name="XCircleIcon" size={16} />
                  {cancelling ? 'Cancellazione...' : 'Cancella Abbonamento'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
